import { useCallback, useEffect, useState } from 'react';
import { GetAdminFeedbacksAPI, GetAdminNpsAPI } from '../../../../api/AdminAPI';

const DEFAULT_FEEDBACKS_SUMMARY = { total: 0, problems: 0, improvements: 0, received: 0, triaged: 0, fixed: 0, released: 0, answered: 0 };
const DEFAULT_NPS_SUMMARY = { total: 0, score: 0, average: null, promoters: 0, passives: 0, detractors: 0, new: 0 };
const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 20, pages: 1 };
const SEARCH_DEBOUNCE_MS = 400;

/**
 * useAdminFeedbacks
 *
 * Hook responsável pelo estado das abas "Feedbacks" e "Avaliações (NPS)" do Admin Geral:
 * filtros (com busca debounced), paginação e dados de cada aba, mantidos independentes
 * para preservar o estado ao alternar de aba.
 *
 * @returns {Object} estado e ações das duas abas, mais `activeTab`/`setActiveTab`
 */
function useAdminFeedbacks() {
  const [activeTab, setActiveTab] = useState('feedbacks');

  // ─── Aba Feedbacks ──────────────────────────────────────────────────────
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbacksSummary, setFeedbacksSummary] = useState(DEFAULT_FEEDBACKS_SUMMARY);
  const [feedbacksPagination, setFeedbacksPagination] = useState(DEFAULT_PAGINATION);
  const [feedbacksLoading, setFeedbacksLoading] = useState(true);
  const [feedbacksError, setFeedbacksError] = useState(null);

  const [feedbackSearchInput, setFeedbackSearchInput] = useState('');
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [feedbackType, setFeedbackTypeState] = useState('');
  const [feedbackStatus, setFeedbackStatusState] = useState('');
  const [feedbackPage, setFeedbackPageState] = useState(1);
  const [feedbackLimit] = useState(20);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFeedbackSearch(feedbackSearchInput);
      setFeedbackPageState(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [feedbackSearchInput]);

  const setFeedbackType = useCallback((value) => {
    setFeedbackTypeState(value);
    setFeedbackPageState(1);
  }, []);

  const setFeedbackStatus = useCallback((value) => {
    setFeedbackStatusState(value);
    setFeedbackPageState(1);
  }, []);

  const setFeedbackPage = useCallback((value) => {
    setFeedbackPageState(value);
  }, []);

  const fetchFeedbacks = useCallback(() => {
    setFeedbacksLoading(true);
    setFeedbacksError(null);
    GetAdminFeedbacksAPI(
      { type: feedbackType, status: feedbackStatus, search: feedbackSearch, page: feedbackPage, limit: feedbackLimit },
      (response) => {
        if (!response || response.success === false) {
          setFeedbacksError(response?.error || 'Erro ao carregar feedbacks. Tente novamente.');
          setFeedbacksLoading(false);
          return;
        }
        setFeedbacks(response.feedbacks || []);
        setFeedbacksSummary(response.summary || DEFAULT_FEEDBACKS_SUMMARY);
        setFeedbacksPagination(response.pagination || { ...DEFAULT_PAGINATION, page: feedbackPage, limit: feedbackLimit });
        setFeedbacksLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackType, feedbackStatus, feedbackSearch, feedbackPage, feedbackLimit]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  // ─── Aba NPS ────────────────────────────────────────────────────────────
  const [nps, setNps] = useState([]);
  const [npsSummary, setNpsSummary] = useState(DEFAULT_NPS_SUMMARY);
  const [npsPagination, setNpsPagination] = useState(DEFAULT_PAGINATION);
  const [npsLoading, setNpsLoading] = useState(true);
  const [npsError, setNpsError] = useState(null);

  const [npsSearchInput, setNpsSearchInput] = useState('');
  const [npsSearch, setNpsSearch] = useState('');
  const [npsStatus, setNpsStatusState] = useState('');
  const [npsPage, setNpsPageState] = useState(1);
  const [npsLimit] = useState(20);

  useEffect(() => {
    const timer = setTimeout(() => {
      setNpsSearch(npsSearchInput);
      setNpsPageState(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [npsSearchInput]);

  const setNpsStatus = useCallback((value) => {
    setNpsStatusState(value);
    setNpsPageState(1);
  }, []);

  const setNpsPage = useCallback((value) => {
    setNpsPageState(value);
  }, []);

  const fetchNps = useCallback(() => {
    setNpsLoading(true);
    setNpsError(null);
    GetAdminNpsAPI(
      { status: npsStatus, search: npsSearch, page: npsPage, limit: npsLimit },
      (response) => {
        if (!response || response.success === false) {
          setNpsError(response?.error || 'Erro ao carregar avaliações NPS. Tente novamente.');
          setNpsLoading(false);
          return;
        }
        setNps(response.nps || []);
        setNpsSummary(response.summary || DEFAULT_NPS_SUMMARY);
        setNpsPagination(response.pagination || { ...DEFAULT_PAGINATION, page: npsPage, limit: npsLimit });
        setNpsLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npsStatus, npsSearch, npsPage, npsLimit]);

  useEffect(() => {
    fetchNps();
  }, [fetchNps]);

  return {
    activeTab,
    setActiveTab,

    feedbacks,
    feedbacksSummary,
    feedbacksPagination,
    feedbacksLoading,
    feedbacksError,
    feedbackSearchInput,
    setFeedbackSearchInput,
    feedbackType,
    setFeedbackType,
    feedbackStatus,
    setFeedbackStatus,
    setFeedbackPage,
    refreshFeedbacks: fetchFeedbacks,

    nps,
    npsSummary,
    npsPagination,
    npsLoading,
    npsError,
    npsSearchInput,
    setNpsSearchInput,
    npsStatus,
    setNpsStatus,
    setNpsPage,
    refreshNps: fetchNps,
  };
}

export default useAdminFeedbacks;
